import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { USOS } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const EMPTY = { clave: "", lote: "", uso: "Otro", superficie_m2: "", llaves_hotel: "", llaves_condo: "", descripcion: "" };

export default function UpeFormModal({ open, onClose, pid, upe, onSaved }) {
  const editing = !!upe;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(upe ? {
        clave: upe.clave || "", lote: upe.lote || "", uso: upe.uso || "Otro",
        superficie_m2: upe.superficie_m2 ?? "", llaves_hotel: upe.llaves_hotel ?? "",
        llaves_condo: upe.llaves_condo ?? "", descripcion: upe.descripcion || "",
      } : EMPTY);
    }
  }, [open, upe]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clave.trim()) return toast.error("La clave de la UPE es obligatoria");
    setSaving(true);
    try {
      const payload = {
        ...form,
        superficie_m2: Number(form.superficie_m2) || 0,
        llaves_hotel: Number(form.llaves_hotel) || 0,
        llaves_condo: Number(form.llaves_condo) || 0,
      };
      if (editing) {
        await api.patch(`/upes/${upe.id}`, payload);
      } else {
        await api.post(`/properties/${pid}/upes`, payload);
      }
      toast.success(editing ? "UPE actualizada" : "UPE creada");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="upe-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-[#002B49]">{editing ? `Editar ${upe.clave}` : "Nueva UPE / unidad"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Clave *</Label>
              <Input data-testid="upe-clave-input" value={form.clave} onChange={(e) => setForm({ ...form, clave: e.target.value })} placeholder="UPE-1" required />
            </div>
            <div className="space-y-1.5">
              <Label>Lote</Label>
              <Input data-testid="upe-lote-input" value={form.lote} onChange={(e) => setForm({ ...form, lote: e.target.value })} placeholder="Lote 1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Uso</Label>
              <Select value={form.uso} onValueChange={(v) => setForm({ ...form, uso: v })}>
                <SelectTrigger data-testid="upe-uso-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {USOS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Superficie (m²)</Label>
              <Input data-testid="upe-superficie-input" type="number" min="0" step="any" value={form.superficie_m2} onChange={(e) => setForm({ ...form, superficie_m2: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Llaves hotel</Label>
              <Input data-testid="upe-llaves-hotel-input" type="number" min="0" value={form.llaves_hotel} onChange={(e) => setForm({ ...form, llaves_hotel: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Unidades condominio</Label>
              <Input data-testid="upe-llaves-condo-input" type="number" min="0" value={form.llaves_condo} onChange={(e) => setForm({ ...form, llaves_condo: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Descripción</Label>
            <Textarea data-testid="upe-descripcion-input" rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button data-testid="upe-guardar-button" type="submit" disabled={saving} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear UPE"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
